import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MenuSheet from "@/components/MenuSheet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  ArrowLeft,
  Sparkles,
  Check,
  Crown,
  Smartphone,
  Heart,
  Infinity as InfinityIcon,
  Wand2,
  ShieldCheck,
} from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { configurePurchases, purchasePremium, hasPremium } from "@/lib/purchases";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const WEB_BENEFITS = [
  {
    icon: InfinityIcon,
    title: "14-day free trial",
    body: "No card required to start. Try every Premium feature with zero risk.",
  },
  {
    icon: Wand2,
    title: "Premium themes & patterns",
    body: "Unlock the full palette library plus future themes as we add them.",
  },
  {
    icon: Sparkles,
    title: "Future Premium features",
    body: "Streaks, smart insights, and everything we add to the Premium tier.",
  },
  {
    icon: Heart,
    title: "Support indie development",
    body: "Lumivera is built by one person. Your subscription keeps it ad-free and yours.",
  },
];

const NATIVE_BENEFITS = [
  {
    icon: InfinityIcon,
    title: "Lifetime access",
    body: "One payment, forever. No subscriptions, no surprises.",
  },
  {
    icon: Wand2,
    title: "Premium themes & patterns",
    body: "Unlock the full palette library plus future themes as we add them.",
  },
  {
    icon: Sparkles,
    title: "Future Premium features",
    body: "Streaks, smart insights, and everything we add to the Premium tier.",
  },
  {
    icon: Heart,
    title: "Support indie development",
    body: "Lumivera is built by one person. Your purchase keeps it ad-free and yours.",
  },
];

export default function UpgradePage() {
  const { user, fetchMe } = useAuth();
  const isNative = Capacitor.isNativePlatform();
  const [loading, setLoading] = useState(false);
  const [owned, setOwned] = useState(Boolean(user?.is_premium));
  const [checking, setChecking] = useState(true);
  const [checkoutError, setCheckoutError] = useState("");
  const [postCheckout, setPostCheckout] = useState(null); // "success" | "cancelled" | null

  // Sync premium status from auth user (web) — native still uses RevenueCat plugin.
  useEffect(() => {
    if (!isNative) setOwned(Boolean(user?.is_premium));
  }, [isNative, user?.is_premium]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");
    if (status === "success" || status === "cancelled") {
      setPostCheckout(status);
      // Clean URL.
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (status === "success") {
      // Poll auth/me up to ~12s to wait for the webhook to land.
      let cancelled = false;
      (async () => {
        for (let i = 0; i < 6; i++) {
          await new Promise((r) => setTimeout(r, 2000));
          if (cancelled) return;
          await fetchMe();
        }
      })();
      return () => {
        cancelled = true;
      };
    }
  }, [fetchMe]);

  useEffect(() => {
    (async () => {
      if (!isNative) {
        setChecking(false);
        return;
      }
      try {
        await configurePurchases(user?.id);
        const has = await hasPremium();
        setOwned(has);
      } catch (e) {
        // Plugin not configured (no API keys yet) — fall through to upsell UI.
      } finally {
        setChecking(false);
      }
    })();
  }, [isNative, user?.id]);

  const buy = async () => {
    if (!isNative) {
      toast.error("Premium is purchased through the iOS or Android app.");
      return;
    }
    setLoading(true);
    try {
      const res = await purchasePremium();
      const has = Boolean(res?.customerInfo?.entitlements?.active?.premium);
      setOwned(has);
      if (has) toast.success("Welcome to Lumivera Premium!");
    } catch (e) {
      const cancelled = e?.userCancelled || /cancel/i.test(e?.message || "");
      if (!cancelled) toast.error("Purchase failed", { description: e?.message });
    } finally {
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/portal-session", {
        return_url: `${window.location.origin}/upgrade`,
      });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error("Couldn't open billing portal", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const startCheckout = async (plan) => {
    // On native iOS/Android we go through Apple/Google IAP via RevenueCat.
    if (isNative) {
      return buy();
    }
    // On the web, Stripe Checkout. Backend endpoint is /api/billing/create-checkout-session.
    setCheckoutError("");
    setLoading(true);
    try {
      const { data } = await api.post("/billing/create-checkout-session", {
        plan, // "monthly" | "yearly"
        success_url: `${window.location.origin}/upgrade?status=success`,
        cancel_url: `${window.location.origin}/upgrade?status=cancelled`,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      if (e?.response?.status === 503 || /not configured/i.test(detail)) {
        setCheckoutError("Checkout isn't set up yet — your Lumivera admin needs to add a Stripe API key.");
      } else {
        setCheckoutError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-2xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="flex items-center gap-2">
          <MenuSheet />
          <Button variant="ghost" size="sm" asChild className="rounded-xl" data-testid="upgrade-back">
            <Link to="/">
              <ArrowLeft strokeWidth={1.5} className="size-4 mr-1.5" />
              Back to planner
            </Link>
          </Button>
        </div>

        {/* Hero */}
        <div
          className="rounded-3xl border-2 border-primary/40 bg-card p-6 sm:p-8 relative overflow-hidden"
          data-testid="upgrade-hero"
          style={{
            backgroundImage:
              "radial-gradient(circle at 100% 0%, hsl(var(--primary) / 0.18), transparent 55%), radial-gradient(circle at 0% 100%, hsl(var(--primary) / 0.10), transparent 55%)",
          }}
        >
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
            <Crown strokeWidth={1.5} className="size-3.5" />
            Lumivera Premium
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-1">
            {isNative ? "One purchase. Yours forever." : "Plan deeper. Live lighter."}
          </h1>
          <p className="text-base text-muted-foreground mt-2 max-w-prose">
            {isNative
              ? "Lumivera will always be useful for free. Premium is for the people who want more — and who want to keep this app independent."
              : "Lumivera is free to use forever. Premium unlocks unlimited vision boards, Sunday Reset, custom theme combos and every Premium feature we ship — starting with a 14-day free trial, no card required."}
          </p>

          {postCheckout === "success" && !owned && (
            <div
              className="mt-6 rounded-2xl border-2 border-primary/40 bg-primary/10 p-4"
              data-testid="upgrade-pending-banner"
            >
              <div className="font-semibold">Confirming your subscription…</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                This takes a few seconds. Premium features unlock automatically.
              </div>
            </div>
          )}
          {postCheckout === "cancelled" && !owned && (
            <div
              className="mt-6 rounded-2xl border border-border bg-muted/40 p-4 text-sm"
              data-testid="upgrade-cancelled-banner"
            >
              Checkout cancelled — no charge. You can try again whenever you&apos;re ready.
            </div>
          )}

          {owned ? (
            <div
              className="mt-6 rounded-2xl border-2 border-primary/40 bg-primary/10 p-4 flex items-center gap-3"
              data-testid="upgrade-owned-banner"
            >
              <div className="size-10 rounded-xl bg-primary/20 flex items-center justify-center">
                <Check strokeWidth={2} className="size-5 text-primary" />
              </div>
              <div className="flex-1">
                <div className="font-semibold">You&apos;re a Premium member</div>
                <div className="text-xs text-muted-foreground">
                  {user?.subscription_status === "trialing" && user?.trial_end
                    ? `Free trial ends ${new Date(user.trial_end).toLocaleDateString()}`
                    : "Thank you for supporting Lumivera."}
                </div>
              </div>
              {!isNative && (
                <Button
                  onClick={openPortal}
                  disabled={loading}
                  variant="outline"
                  size="sm"
                  className="rounded-xl"
                  data-testid="upgrade-manage-button"
                >
                  Manage
                </Button>
              )}
            </div>
          ) : (
            <div className="mt-6 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => startCheckout("yearly")}
                  disabled={loading || checking}
                  className="rounded-2xl border-2 border-primary bg-primary/5 hover:bg-primary/10 p-4 text-left transition-colors relative"
                  data-testid="upgrade-buy-yearly"
                >
                  <span className="absolute -top-2.5 right-3 text-[10px] font-bold uppercase tracking-wider bg-primary text-primary-foreground rounded-full px-2 py-0.5">
                    Best value
                  </span>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
                    Annual
                  </div>
                  <div className="font-display text-2xl font-extrabold tracking-tight mt-1">
                    $39.99 <span className="text-sm font-normal text-muted-foreground">/year</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Save ~17% &middot; 14-day free trial
                  </div>
                </button>
                <button
                  onClick={() => startCheckout("monthly")}
                  disabled={loading || checking}
                  className="rounded-2xl border-2 border-border hover:border-primary/60 p-4 text-left transition-colors"
                  data-testid="upgrade-buy-monthly"
                >
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
                    Monthly
                  </div>
                  <div className="font-display text-2xl font-extrabold tracking-tight mt-1">
                    $3.99 <span className="text-sm font-normal text-muted-foreground">/mo</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    14-day free trial &middot; cancel anytime
                  </div>
                </button>
              </div>
              {checkoutError && (
                <p className="text-xs text-destructive" data-testid="upgrade-error">{checkoutError}</p>
              )}
            </div>
          )}
        </div>

        <Card className="rounded-2xl" data-testid="premium-included">
          <CardContent className="pt-5 space-y-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
              What you get with premium
            </div>
            <ul className="space-y-2 text-sm">
              {[
                "Unlimited vision boards (free tier has 1)",
                "Sunday Reset — weekly Reflect / Prepare / Dream / Plan",
                "Custom theme presets — pick any hex colour for primary + background",
                "Save your custom combos with a name & sync across devices",
                "Every background pattern (polka dots, soft waves, leaves, confetti, more)",
                "Priority support and every Premium feature we ship next",
              ].map((line) => (
                <li key={line} className="flex items-start gap-2">
                  <Check strokeWidth={2} className="size-4 text-primary mt-0.5 shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Benefits */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="upgrade-benefits">
          {(isNative ? NATIVE_BENEFITS : WEB_BENEFITS).map((b) => {
            const Icon = b.icon;
            return (
              <Card key={b.title} className="rounded-2xl">
                <CardContent className="pt-5 space-y-2">
                  <div className="size-10 rounded-xl bg-primary/15 flex items-center justify-center">
                    <Icon strokeWidth={1.5} className="size-5 text-primary" />
                  </div>
                  <div className="font-display text-lg font-bold tracking-tight">{b.title}</div>
                  <p className="text-sm text-muted-foreground">{b.body}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Fine print */}
        <Card className="rounded-2xl">
          <CardContent className="pt-5 text-xs text-muted-foreground space-y-2">
            <div className="flex items-start gap-2">
              <ShieldCheck strokeWidth={1.5} className="size-4 text-primary shrink-0 mt-0.5" />
              <p className="m-0">
                {isNative
                  ? "Payment is processed by Apple or Google, never Lumivera. We don't store your card. Premium is tied to your store account and restorable on any device you sign in with."
                  : "Payment is processed by Stripe, never Lumivera — we don't store your card. Subscriptions auto-renew until you cancel; you can cancel anytime in your billing portal. Free trial converts to a paid plan only if you add a card before it ends."}
              </p>
            </div>
            <div className="pl-6">
              By {isNative ? "purchasing" : "subscribing"} you agree to our{" "}
              <Link to="/terms" className="underline">Terms</Link> and{" "}
              <Link to="/privacy" className="underline">Privacy Policy</Link>.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
